import { InformationIcon } from '@blocksuite/icons/rc';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { TooltipContentProps } from '@radix-ui/react-tooltip';
import { cssVar } from '@toeverything/theme';
import clsx from 'clsx';
import {
  cloneElement,
  type CSSProperties,
  type KeyboardEvent,
  type KeyboardEventHandler,
  type MouseEvent,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
  useSyncExternalStore,
} from 'react';

import { Tooltip } from '../tooltip';
import * as styles from './styles.css';

// Hover capability is a RUNTIME question (a build-time mobile flag is false in a web bundle
// served to phones): a touch-capable device gets a tap-to-open popover, because radix
// tooltips never open from touch (the trigger drops pointerType === 'touch'). any-pointer
// catches hybrids (touchscreen laptops/tablets with a mouse) — there a click opens the tip,
// which every pointer on the device can produce; a hover-only branch would leave the ⓘ dead
// under the finger.
const COARSE_QUERY = '(hover: none), (pointer: coarse), (any-pointer: coarse)';

function subscribeCoarse(cb: () => void) {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => {};
  }
  const mql = window.matchMedia(COARSE_QUERY);
  mql.addEventListener('change', cb);
  return () => mql.removeEventListener('change', cb);
}

function readCoarse() {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.(COARSE_QUERY).matches
  );
}

// Never viewport-clipped: radix flips/shifts the box (avoidCollisions is on by default) and we
// cap it at the collision-aware budget radix publishes for the open direction, scrolling inside
// when the copy is longer. Each radix flavour defines its own var; the unmatched one falls back
// so min() always resolves to the active flavour's budget. Inline so it also overrides the base
// tooltipContent maxWidth on the hover branch regardless of stylesheet order.
const sizeStyle: CSSProperties = {
  maxWidth:
    'min(var(--radix-tooltip-content-available-width, 100vw), var(--radix-popover-content-available-width, 100vw), 320px)',
  maxHeight:
    'min(var(--radix-tooltip-content-available-height, 100vh), var(--radix-popover-content-available-height, 100vh), 360px)',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  whiteSpace: 'pre-line',
};

export interface InfoTipProps {
  /** The explanation. Falsy → the trigger renders bare (custom children) or not at all. */
  content?: ReactNode;
  /** Accessible name for the default icon trigger. */
  label: string;
  side?: TooltipContentProps['side'];
  align?: TooltipContentProps['align'];
  /** Optional custom trigger element (must take a ref — radix asChild). Default: an ⓘ button. */
  children?: ReactElement;
  /** Extra class for the default icon trigger. */
  className?: string;
  'data-testid'?: string;
}

/**
 * A small "what is this?" affordance: an info icon (or a custom trigger) that explains a control
 * in plain language. Hover/focus opens it on pointer-precise devices; on touch devices it opens
 * on tap and closes on tap-outside/Escape. The bubble auto-flips at viewport edges and scrolls
 * internally when the text is long.
 */
export const InfoTip = ({
  content,
  label,
  side = 'top',
  align = 'center',
  children,
  className,
  'data-testid': testId,
}: InfoTipProps) => {
  const coarse = useSyncExternalStore(subscribeCoarse, readCoarse, () => false);

  if (!content) {
    return children ?? null;
  }

  const trigger = children ?? (
    <button
      type="button"
      aria-label={label}
      className={clsx(styles.trigger, className)}
      data-testid={testId ?? 'info-tip-trigger'}
    >
      <InformationIcon />
    </button>
  );

  if (!coarse) {
    return (
      <Tooltip
        content={content}
        side={side}
        align={align}
        rootOptions={{ delayDuration: 150 }}
        options={{ collisionPadding: 8, style: sizeStyle }}
      >
        {trigger}
      </Tooltip>
    );
  }

  // A tap on the trigger must never activate a surrounding control (a stage tile, a row
  // button) — the tip consumes its own tap. Radix composes the cloned handler before its
  // own toggle, so stopping propagation never suppresses the open. A CUSTOM trigger (a
  // plain span) additionally gets button semantics so keyboard users can open it.
  const triggerProps = trigger.props as {
    onClick?: MouseEventHandler<HTMLElement>;
    onKeyDown?: KeyboardEventHandler<HTMLElement>;
  };
  const tapTrigger = cloneElement(trigger, {
    onClick: (e: MouseEvent<HTMLElement>) => {
      triggerProps.onClick?.(e);
      e.stopPropagation();
    },
    ...(children
      ? {
          role: 'button',
          onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
            triggerProps.onKeyDown?.(e);
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.currentTarget.click();
            }
          },
        }
      : null),
  });

  return (
    // modal: while a tip is open the page underneath is inert, so the dismissing tap only
    // closes the tip — it can never activate the control under the finger, and two tips
    // can never stack.
    <PopoverPrimitive.Root modal>
      <PopoverPrimitive.Trigger asChild>{tapTrigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className={styles.content}
          side={side}
          align={align}
          sideOffset={5}
          collisionPadding={8}
          style={{ zIndex: cssVar('zIndexPopover'), ...sizeStyle }}
        >
          {content}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};
